import camelcaseKeys from 'camelcase-keys'
import { create } from 'zustand'

import type { Project } from '@/modules/projects'
import {
  executeDelete,
  executeGet,
  executePost,
  executePut,
} from '@/services/lukita'
import { removeItems, updateItem } from '@/utils'
import { isNewFolderScope } from './folderScope'
import type { Folder, FolderDTO, FolderKind, FoldersStore } from './types'

export const useFoldersStore = create<FoldersStore>((set, get, store) => ({
  loadingFolder: true,
  loadingFolders: true,
  mutating: false,
  folder: null,
  folders: [],
  foldersKind: null,

  actions: {
    reset: () => set(store.getInitialState()),
    startLoadingFolder: () => set({ loadingFolder: true }),
    stopLoadingFolder: () => set({ loadingFolder: false }),
    startLoadingFolders: () => set({ loadingFolders: true }),
    stopLoadingFolders: () => set({ loadingFolders: false }),

    setFolder: (folder) =>
      set(() => {
        if (!folder) return { folder: null }
        return { folder: camelcaseKeys(folder) }
      }),

    updateFolder: (folder) =>
      set((state) => {
        const camelized = camelcaseKeys(folder)
        if (state.folder) return { folder: camelized }
        return { folders: updateItem(state.folders, camelized) }
      }),

    setFolders: (folders, kind) =>
      set({ folders: camelcaseKeys(folders), foldersKind: kind }),

    addFolder: (folder) =>
      set((state) => ({
        folders: [
          camelcaseKeys(folder as Folder | FolderDTO),
          ...state.folders,
        ],
      })),

    deleteFolder: (id) =>
      set((state) => ({
        folders: removeItems(state.folders, [id]),
      })),
  },

  mutations: {
    createFolder: async (body) => {
      set({ mutating: true })
      const response = await executePost('/folders', body).finally(() =>
        set({ mutating: false }),
      )
      const raw = await response.json()
      const folder = camelcaseKeys(raw) as Folder
      get().actions.addFolder(folder)
      return folder
    },

    updateFolder: async (slug, body) => {
      set({ mutating: true })
      const response = await executePut(`/folders/${slug}`, body).finally(() =>
        set({ mutating: false }),
      )
      const raw = await response.json()
      const folder = camelcaseKeys(raw) as Folder
      get().actions.setFolder(folder)
      return folder
    },

    deleteFolder: async (folder) => {
      get().actions.deleteFolder(folder.id)
      try {
        await executeDelete(`/folders/${folder.slug}`)
      } catch (error) {
        get().actions.addFolder(folder)
        throw error
      }
    },

    combineFolder: async (slug) => {
      set({ mutating: true })
      const response = await executePost(`/folders/${slug}/combine`).finally(
        () => set({ mutating: false }),
      )
      const raw = await response.json()
      return camelcaseKeys(raw) as Project
    },

    archiveFolder: async (slug) => {
      await executePost(`/folders/${slug}/archive`)
      await get().loaders.fetchFolders('project')
    },
  },

  loaders: {
    fetchFolder: async (slug) => {
      const { actions, folder } = get()
      if (folder?.slug !== slug) {
        actions.startLoadingFolder()
      }
      const response = await executeGet(`/folders/${slug}`)
      const result = await response.json()
      actions.setFolder(result)
      actions.stopLoadingFolder()
    },

    fetchFolders: async (kind: FolderKind) => {
      const { actions, folders, foldersKind } = get()

      if (
        isNewFolderScope({
          loadedKind: foldersKind,
          requestedKind: kind,
          loadedCount: folders.length,
        })
      ) {
        actions.startLoadingFolders()
      }

      const response = await executeGet('/folders', { queryParams: { kind } })
      const result = await response.json()
      actions.setFolders(result, kind)
      actions.stopLoadingFolders()
    },
  },
}))
